import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { useCallback } from 'react';
import { useErrorHandler } from './useErrorHandler';
import { extractSerializableProps } from '../utils/debug';

interface UseValidatedFormOptions<T extends Record<string, any>> {
  schema: z.ZodSchema<T>;
  onSubmit: (data: T) => Promise<void> | void;
  onError?: (error: any) => void;
  defaultValues?: Partial<T>;
  mode?: 'onChange' | 'onBlur' | 'onSubmit' | 'onTouched' | 'all';
}

/**
 * Hook personalizado que combina react-hook-form com Zod para validação
 * e tratamento de erros integrado
 */
export function useValidatedForm<T extends Record<string, any>>({
  schema,
  onSubmit,
  onError,
  defaultValues,
  mode = 'onChange'
}: UseValidatedFormOptions<T>) {
  
  const { handleError, clearError, hasError, errorMessage } = useErrorHandler();
  
  const form = useForm({
    // @ts-ignore - Incompatibilidade temporária entre versões do zod e react-hook-form
    resolver: zodResolver(schema),
    defaultValues: defaultValues as any,
    mode
  });

  const { handleSubmit: rhfHandleSubmit, formState: { isSubmitting } } = form;

  const handleSubmit = useCallback(
    (e?: React.BaseSyntheticEvent) => {
      clearError();
      
      return rhfHandleSubmit(
        async (data: any) => {
          try {
            await onSubmit(data as T);
          } catch (error) {
            // Usar função helper para extrair apenas propriedades serializáveis
            const safeFormData = extractSerializableProps(data);

            handleError(error, { 
              formData: safeFormData, 
              action: 'form_submit',
              timestamp: new Date().toISOString()
            });
            
            if (onError) {
              onError(error);
            }
          }
        },
        (errors) => {
          console.warn('Validation errors:', errors);
          if (onError) {
            onError(errors);
          }
        }
      )(e);
    },
    [rhfHandleSubmit, onSubmit, onError, handleError, clearError]
  );

  const getFieldError = useCallback((fieldName: string): string | undefined => {
    const error = form.formState.errors[fieldName];
    return error?.message as string | undefined;
  }, [form.formState.errors]);

  const getFieldState = useCallback((fieldName: string) => {
    const error = getFieldError(fieldName);
    const isInvalid = !!error;
    const isDirty = !!(form.formState.dirtyFields as any)[fieldName];
    const isTouched = !!(form.formState.touchedFields as any)[fieldName];
    
    return {
      error,
      isInvalid,
      isDirty,
      isTouched,
      showError: isInvalid && (isDirty || isTouched),
      inputProps: {
        'aria-invalid': isInvalid,
        'aria-describedby': error ? `${fieldName}-error` : undefined
      }
    };
  }, [form.formState, getFieldError]);

  return {
    // Propriedades do form
    register: form.register,
    control: form.control,
    watch: form.watch,
    setValue: form.setValue,
    getValues: form.getValues,
    reset: form.reset,
    formState: form.formState,
    
    // Funcionalidades customizadas
    handleSubmit,
    isSubmitting,
    hasErrors: hasError || Object.keys(form.formState.errors).length > 0,
    submitError: errorMessage,
    clearSubmitError: clearError,
    getFieldError,
    getFieldState
  };
}

/**
 * Helper para criar um componente de input com validação
 */
export function createValidatedInput<T extends Record<string, any>>(
  form: ReturnType<typeof useValidatedForm<T>>,
  fieldName: keyof T,
  options?: {
    label?: string;
    type?: string;
    placeholder?: string;
    className?: string;
  }
) {
  const fieldState = form.getFieldState(fieldName as string);
  
  return {
    ...(form.register as any)(fieldName as string),
    ...fieldState.inputProps,
    className: `${options?.className || ''} ${fieldState.isInvalid ? 'border-red-500' : ''}`.trim(),
    type: options?.type || 'text',
    placeholder: options?.placeholder,
    error: fieldState.error,
    showError: fieldState.showError
  };
}